import { useState, type FormEvent, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MOVEMENT_CATEGORIES,
  MOVEMENT_CATEGORY_LABELS,
  type CreateMovementTypeInput,
  type MovementCategory,
  type UpdateMovementTypeInput,
} from '@kn/shared';
import { api } from '../../api/client';
import { messageErreur } from '../../api/error-message';
import { useAuth } from '../../auth/auth-context';

const MOVEMENT_TYPES_QUERY_KEY = ['movement-types'] as const;

const FORMULAIRE_VIDE: CreateMovementTypeInput = {
  codeMouvement: '',
  motif: '',
  libelle: '',
};

/** Rend le tri-etat `declarable` (oui / non / non qualifie) manipulable. */
function declarableVersTexte(valeur: boolean | null): string {
  return valeur === null ? 'null' : valeur ? 'true' : 'false';
}

function texteVersDeclarable(valeur: string): boolean | null {
  return valeur === 'null' ? null : valeur === 'true';
}

export function MovementTypesPage(): ReactElement {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const peutModifier = user?.role === 'ADMIN';

  const [recherche, setRecherche] = useState('');
  const [aQualifier, setAQualifier] = useState(false);
  const [formulaireOuvert, setFormulaireOuvert] = useState(false);
  const [formulaire, setFormulaire] = useState<CreateMovementTypeInput>(FORMULAIRE_VIDE);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const listeQuery = useQuery({
    queryKey: [...MOVEMENT_TYPES_QUERY_KEY, recherche, aQualifier],
    queryFn: () =>
      api.listMovementTypes({
        search: recherche || undefined,
        aQualifier: aQualifier || undefined,
        pageSize: 200,
      }),
  });

  function rafraichir(texte: string): void {
    setErreur(null);
    setMessage(texte);
    void queryClient.invalidateQueries({ queryKey: MOVEMENT_TYPES_QUERY_KEY });
  }

  const creation = useMutation({
    mutationFn: (input: CreateMovementTypeInput) => api.createMovementType(input),
    onSuccess: (cree) => {
      setFormulaire(FORMULAIRE_VIDE);
      setFormulaireOuvert(false);
      rafraichir(`Couple ${cree.codeMouvement}/${cree.motif || '(sans motif)'} créé.`);
    },
    onError: (error: unknown) => setErreur(messageErreur(error)),
  });

  const modification = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateMovementTypeInput }) =>
      api.updateMovementType(id, patch),
    onSuccess: (modifie) =>
      rafraichir(`Couple ${modifie.codeMouvement}/${modifie.motif || '(sans motif)'} mis à jour.`),
    onError: (error: unknown) => setErreur(messageErreur(error)),
  });

  function handleCreation(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setErreur(null);
    setMessage(null);
    creation.mutate(formulaire);
  }

  const enCours = creation.isPending || modification.isPending;

  return (
    <>
      <h1>Référentiels &mdash; Types de mouvements</h1>
      <p className="sous-titre">
        Paramétrage des couples Mouvement &times; Motif. Les couples observes dans les fichiers
        seront créés automatiquement en catégorie &laquo;&nbsp;a qualifier&nbsp;&raquo;. Tant
        qu&apos;un couple n&apos;est pas qualifie, il n&apos;influence aucun calcul. Edition
        réservée aux administrateurs, chaque modification est tracée.
      </p>

      {erreur && (
        <p className="alerte alerte--erreur" role="alert">
          {erreur}
        </p>
      )}
      {message && <p className="alerte alerte--succes">{message}</p>}

      <div className="carte">
        <div className="barre-actions">
          <label className="champ" htmlFor="recherche-mouvement">
            <span>Rechercher</span>
            <input
              id="recherche-mouvement"
              type="search"
              placeholder="Code, motif ou libellé"
              value={recherche}
              onChange={(event) => setRecherche(event.target.value)}
            />
          </label>
          <label className="champ" htmlFor="filtre-a-qualifier">
            <span>Filtre</span>
            <select
              id="filtre-a-qualifier"
              value={aQualifier ? 'oui' : 'non'}
              onChange={(event) => setAQualifier(event.target.value === 'oui')}
            >
              <option value="non">Tous les couples</option>
              <option value="oui">À qualifier uniquement</option>
            </select>
          </label>
          {peutModifier && (
            <button
              type="button"
              className="bouton"
              onClick={() => setFormulaireOuvert((ouvert) => !ouvert)}
            >
              {formulaireOuvert ? 'Annuler' : 'Nouveau couple'}
            </button>
          )}
        </div>

        {formulaireOuvert && peutModifier && (
          <form onSubmit={handleCreation} noValidate>
            <div className="barre-actions">
              <label className="champ" htmlFor="nouveau-code">
                <span>Code mouvement</span>
                <input
                  id="nouveau-code"
                  type="text"
                  value={formulaire.codeMouvement}
                  onChange={(event) =>
                    setFormulaire((etat) => ({ ...etat, codeMouvement: event.target.value }))
                  }
                />
              </label>
              <label className="champ" htmlFor="nouveau-motif">
                <span>Motif</span>
                <input
                  id="nouveau-motif"
                  type="text"
                  value={formulaire.motif ?? ''}
                  onChange={(event) =>
                    setFormulaire((etat) => ({ ...etat, motif: event.target.value }))
                  }
                />
              </label>
              <label className="champ" htmlFor="nouveau-libelle">
                <span>Libellé</span>
                <input
                  id="nouveau-libelle"
                  type="text"
                  value={formulaire.libelle ?? ''}
                  onChange={(event) =>
                    setFormulaire((etat) => ({ ...etat, libelle: event.target.value }))
                  }
                />
              </label>
              <button type="submit" className="bouton" disabled={enCours}>
                Créer le couple
              </button>
            </div>
            <p className="sous-titre">
              Le couple est créé avec les valeurs neutres&nbsp;: catégorie &laquo;&nbsp;a
              qualifier&nbsp;&raquo;, périmètre déclarable non renseigne, écart non attendu. La
              qualification se fait ensuite ligne par ligne.
            </p>
          </form>
        )}

        {listeQuery.isPending && <p className="chargement">Chargement du paramétrage...</p>}
        {listeQuery.isError && (
          <p className="alerte alerte--erreur">{messageErreur(listeQuery.error)}</p>
        )}

        {listeQuery.data && listeQuery.data.total === 0 && (
          <p className="alerte alerte--info">
            Aucun couple enregistre. La table se remplira automatiquement lors des imports de
            mouvements ; le paramétrage complet (tableau B) sera saisi ulterieurement.
          </p>
        )}

        {listeQuery.data && listeQuery.data.total > 0 && (
          <table className="tableau">
            <caption className="sous-titre">{listeQuery.data.total} couple(s)</caption>
            <thead>
              <tr>
                <th scope="col">Mouvement</th>
                <th scope="col">Motif</th>
                <th scope="col">Libellé</th>
                <th scope="col">Catégorie</th>
                <th scope="col">Déclarable</th>
                <th scope="col">Écart attendu</th>
                <th scope="col">État</th>
              </tr>
            </thead>
            <tbody>
              {listeQuery.data.items.map((couple) => (
                <tr key={couple.id}>
                  <td>{couple.codeMouvement}</td>
                  <td>{couple.motif || <span className="sous-titre">(sans motif)</span>}</td>
                  <td>{couple.libelle ?? '—'}</td>
                  <td>
                    <select
                      aria-label={`Catégorie de ${couple.codeMouvement}/${couple.motif}`}
                      value={couple.categorie}
                      disabled={!peutModifier || enCours}
                      onChange={(event) =>
                        modification.mutate({
                          id: couple.id,
                          patch: { categorie: event.target.value as MovementCategory },
                        })
                      }
                    >
                      {MOVEMENT_CATEGORIES.map((categorie) => (
                        <option key={categorie} value={categorie}>
                          {MOVEMENT_CATEGORY_LABELS[categorie]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      aria-label={`Périmètre déclarable de ${couple.codeMouvement}/${couple.motif}`}
                      value={declarableVersTexte(couple.declarable)}
                      disabled={!peutModifier || enCours}
                      onChange={(event) =>
                        modification.mutate({
                          id: couple.id,
                          patch: { declarable: texteVersDeclarable(event.target.value) },
                        })
                      }
                    >
                      <option value="null">Non qualifié</option>
                      <option value="true">Oui</option>
                      <option value="false">Non</option>
                    </select>
                  </td>
                  <td>
                    <select
                      aria-label={`Écart attendu pour ${couple.codeMouvement}/${couple.motif}`}
                      value={couple.ecartAttendu ? 'oui' : 'non'}
                      disabled={!peutModifier || enCours}
                      onChange={(event) =>
                        modification.mutate({
                          id: couple.id,
                          patch: { ecartAttendu: event.target.value === 'oui' },
                        })
                      }
                    >
                      <option value="non">Non</option>
                      <option value="oui">Oui</option>
                    </select>
                  </td>
                  <td>
                    <span
                      className={`etiquette ${couple.isActive ? 'etiquette--actif' : 'etiquette--inactif'}`}
                    >
                      {couple.isActive ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
